import { createPortal } from 'react-dom';
import { useBodyScrollLock, useModalClose } from '@/common/hooks';
import { CloseButton } from './IconButton';

const SIZE_CLASSES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
  '2xl': 'max-w-4xl',
  full: 'max-w-[1400px]',
};

/**
 * Centered modal shell — backdrop, panel, optional title + close.
 */
export function Modal({
  isOpen,
  onClose,
  children,
  title,
  description,
  size = 'md',
  showClose = true,
  isDirty = false,
  className = '',
  panelClassName = '',
  overlayClassName = '',
  bodyClassName = '',
  zIndexClass = 'z-[60]',
  offsetSidebar = false,
  footer,
}) {
  useBodyScrollLock(isOpen);

  const panelRef = useModalClose({
    isOpen,
    onClose,
    isDirty,
  });

  if (!isOpen || typeof document === 'undefined') return null;

  const content = (
    <div
      className={`fixed inset-0 ${zIndexClass} bg-black/60 flex items-center justify-center p-2.5 sm:p-4 backdrop-blur-sm animate-fadeIn font-sf-pro-display ${
        offsetSidebar ? 'lg:pl-[calc(260px+1rem)]' : ''
      } ${overlayClassName}`}
    >
      <div
        ref={panelRef}
        data-modal-root
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'shared-modal-title' : undefined}
        onMouseDown={(e) => e.stopPropagation()}
        className={`bg-card-background border border-border-default rounded-2xl w-full ${SIZE_CLASSES[size] || SIZE_CLASSES.md} p-4 sm:p-6 relative shadow-2xl text-left text-text-primary max-h-[92vh] sm:max-h-[90vh] overflow-y-auto custom-scrollbar ${panelClassName} ${className}`}
      >
        {showClose && onClose && (
          <CloseButton
            onClick={onClose}
            className="absolute top-4 right-4 z-10"
          />
        )}

        {(title || description) && (
          <div className={`mb-5 shrink-0 ${showClose ? 'pr-8' : ''}`}>
            {title && (
              <h2
                id="shared-modal-title"
                className="text-h6 font-bold text-text-primary"
              >
                {title}
              </h2>
            )}
            {description && (
              <p className="text-body-sm text-text-secondary mt-1">
                {description}
              </p>
            )}
          </div>
        )}

        <div className={bodyClassName || undefined}>{children}</div>

        {footer && (
          <div className="mt-6 flex justify-end gap-3 flex-wrap shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
